import React from "react";
import { Share2, Sparkles, Trophy, Zap, Crown } from "lucide-react";
import CommonButton from "./CommonButton";
import useThemeHook from "../hooks/useThemeHook";
import { THEME_COLORS } from "../themes/DarkLight";

interface ViralShareModalProps {
  grantAmount: number;
  multiplier: number;
  closeModal: () => void;
}

const ViralShareModal: React.FC<ViralShareModalProps> = ({ grantAmount, multiplier, closeModal }) => {
  const theme = useThemeHook();

  const handleShare = async () => {
    const text = `🚀 I just claimed ${grantAmount.toFixed(2)} ATH tokens with a ${multiplier.toFixed(1)}x boost on Arthereum Wallet! 💎 Join me and start mining now! #Arthereum #PiNetwork #Web3`;
    const url = window.location.origin;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Arthereum Mining Win!',
          text: text,
          url: url,
        });
      } catch (err) {
        console.log("Error sharing:", err);
      }
    } else {
      // Fallback: Copy to clipboard
      navigator.clipboard.writeText(`${text} ${url}`);
      alert("Link copied to clipboard! Share it with your friends!");
    }
  };

  return (
    <div className="flex flex-col items-center p-6 space-y-6 text-center">
      <div className="relative">
        <div className="absolute inset-0 bg-yellow-400 blur-2xl opacity-20 animate-pulse"></div>
        <div className="relative w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-[32px] flex items-center justify-center shadow-xl shadow-orange-500/20 rotate-12">
            <Trophy size={48} className="text-white drop-shadow-md" />
        </div>
        <Sparkles className="absolute -top-4 -right-4 text-yellow-500 animate-bounce" size={32} />
      </div>

      <div className="space-y-2">
        <h2 className="text-3xl font-black text-slate-900 leading-tight">
          MASSIVE WIN! <br/>
          <span className="text-orange-600">{multiplier.toFixed(1)}x BOOSTED</span>
        </h2>
        <p className="text-slate-500 text-sm">
          You just claimed a legendary grant. Your mining power is legendary!
        </p>
      </div>

      <div className="w-full bg-slate-50 rounded-3xl p-6 border border-slate-100 relative overflow-hidden">
        <div className="absolute top-0 right-0 p-4 opacity-5">
            <Crown size={80} />
        </div>
        <div className="relative z-10">
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Amount Claimed</span>
            <div className="text-4xl font-black text-slate-900 mt-1">
                {grantAmount.toFixed(4)} <span className="text-lg text-orange-500">ATH</span>
            </div>
            <div className="flex items-center justify-center gap-2 mt-3">
                <div className="bg-orange-100 text-orange-600 text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1">
                    <Zap size={10} fill="currentColor" />
                    LEGENDARY MULTIPLIER
                </div>
            </div>
        </div>
      </div>

      <div className="w-full space-y-3 pt-2">
        <button 
            onClick={handleShare}
            className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black text-lg shadow-lg shadow-indigo-600/20 active:scale-95 transition-all flex items-center justify-center gap-2"
        >
            <Share2 size={20} />
            SHARE & CELEBRATE
        </button>
        <p className="text-[10px] text-slate-400 uppercase font-bold tracking-widest">
            Spread the word and keep the luck coming! ✨
        </p>
      </div>
    </div>
  );
};

export default ViralShareModal;
